import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import CallIcon from '@mui/icons-material/CallOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CalendarClockIcon from 'Assets/Icons/calendar_clock.svg';
import { ROUTES } from 'Navigation';
import { StatusPill, SecondaryStatusPill } from 'v3/Components/Shared/StatusPill';
import ActionMenu from 'v3/Components/Shared/ActionMenu';
import { SecureComponentV3 } from 'Components/Security/SecureComponentV3';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { useGlobalContext, useToasts } from 'Context';
import { buildEndpoint, useDataPoster, formatShortDateOnly } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import FormErrorSection from 'v3/Components/Form/FormErrorSection';
import { ReviewerLifecycleStatus } from 'v3/Models/Enums';
import { ReviewerProfileModel } from './useReviewerProfileForm';
import { ContactInformationData } from './ReviewerProfile.Overview.ContactInformation';
import { LicenseInformationData } from './ReviewerProfile.Overview.LicenseInformation';
import { RatesData } from './ReviewerProfile.Overview.Rates';
import {
  getReviewerProfileMissingSections,
  getMissingSectionsFromErrorResponse,
  pruneResolvedMissingSections,
  MissingSectionError,
  REVIEWER_PROFILE_SECTION_DOCUMENTS,
} from './ReviewerProfile.ProfileCompleteness';
import ReviewerProfileArchiveDialog, { ArchiveReasonOption } from './ReviewerProfile.ArchiveDialog';
import ReviewerProfileReactivateDialog from './ReviewerProfile.ReactivateDialog';
import ReviewerProfilePlaceOnHoldDialog from './ReviewerProfile.PlaceOnHoldDialog';
import ReviewerProfileRemoveHoldDialog from './ReviewerProfile.RemoveHoldDialog';
import ReviewerProfileDiscardDialog from './ReviewerProfile.DiscardDialog';
import ReviewerProfileInviteDialog from './ReviewerProfile.InviteDialog';
import ReviewerProfileNotReadyDialog from './ReviewerProfile.NotReadyDialog';
import ReviewerProfileEditFollowUpDrawer from './ReviewerProfile.EditFollowUpDrawer';
import { ReviewerProfileActionKey, isReviewerActionAvailable } from './ReviewerProfile.StatusActions';
import './ReviewerProfile.Header.scss';

export const ReviewerProfileHeaderSkeleton: React.FC = () => (
  <div className="rp-skeleton-header">
    <div className="rp-skeleton-header__back" />
    <div className="rp-skeleton-header__actions" />
    <div className="rp-skeleton-header__content">
      <div className="rp-skeleton-header__profile-row">
        <div className="rp-skeleton-header__avatar" />
        <div className="rp-skeleton-header__info">
          <div className="rp-skeleton-header__name-row">
            <div className="rp-skeleton-header__name" />
            <div className="rp-skeleton-header__status" />
          </div>
          <div className="rp-skeleton-header__details">
            <div className="rp-skeleton-header__detail-row">
              <div className="rp-skeleton-header__detail-label" />
              <div className="rp-skeleton-header__detail-value" />
              <div className="rp-skeleton-header__detail-value" />
            </div>
            <div className="rp-skeleton-header__detail-row">
              <div className="rp-skeleton-header__detail-label" />
              <div className="rp-skeleton-header__detail-value" />
            </div>
          </div>
        </div>
      </div>
      <div className="rp-skeleton-header__tabs">
        <div className="rp-skeleton-header__tab" />
        <div className="rp-skeleton-header__tab" />
        <div className="rp-skeleton-header__tab" />
      </div>
    </div>
  </div>
);

export enum ReviewerProfileTab {
  Overview = 'Overview',
  Documents = 'Documents',
  Notes = 'Notes',
}

type TabConfig = {
  tab: ReviewerProfileTab;
  permissions: string[];
};

const TABS: TabConfig[] = [
  { tab: ReviewerProfileTab.Overview,   permissions: [PermissionCodesV3.Reviewer.Profile.View] },
  { tab: ReviewerProfileTab.Documents,  permissions: [PermissionCodesV3.Reviewer.Document.View] },
  { tab: ReviewerProfileTab.Notes,      permissions: [PermissionCodesV3.Reviewer.Note.View] },
];

type ReviewerProfileHeaderProps = {
  reviewer: ReviewerProfileModel;
  contactData?: ContactInformationData;
  licenseData?: LicenseInformationData;
  ratesData?: RatesData;
  uploadedDocumentTypeNames?: string[];
  archiveReasons?: ArchiveReasonOption[];
  onReviewerRefetch?: () => void;
};

const ReviewerProfileHeader: React.FC<ReviewerProfileHeaderProps> = React.memo(({
  reviewer,
  contactData,
  licenseData,
  ratesData,
  uploadedDocumentTypeNames,
  archiveReasons = [],
  onReviewerRefetch,
}) => {
  const history = useHistory();
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const toast = useToasts();
  const [globalState] = useGlobalContext();
  const grantedPermissions = globalState.permissionsV3 ?? new Set<string>();
  const [isHoldDialogOpen, setIsHoldDialogOpen] = useState(false);
  const [isRemoveHoldDialogOpen, setIsRemoveHoldDialogOpen] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isCheckingInviteCompleteness, setIsCheckingInviteCompleteness] = useState(false);
  const [isNotReadyDialogOpen, setIsNotReadyDialogOpen] = useState(false);
  const [isEditFollowUpDrawerOpen, setIsEditFollowUpDrawerOpen] = useState(false);
  const [isContinuingOnboarding, setIsContinuingOnboarding] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isReactivateDialogOpen, setIsReactivateDialogOpen] = useState(false);
  const [showMissingFieldsAlert, setShowMissingFieldsAlert] = useState(false);
  // Set only when the backend rejects a request that the client-side check thought was complete
  // (e.g. stale data). Takes precedence over the client-computed list while present.
  const [backendMissingSections, setBackendMissingSections] = useState<MissingSectionError[] | null>(null);
  // The email value at the time a backend validation attempt failed — lets pruneResolvedMissingSections
  // keep the "email already in use" error (which it can't reproduce client-side) until the email
  // actually changes, instead of dropping it on the next unrelated Contact/License/Rates save.
  const [emailAtLastValidation, setEmailAtLastValidation] = useState<string | undefined>();
  const statusValue = reviewer.status?.value as number | undefined;
  const isOnHold = !!reviewer.isOnHold;
  const isProfileComplete = !!reviewer.isProfileComplete;
  // Not Ready is a bookmark, not an incomplete-profile indicator — the banner only ever reflects
  // an actual saved follow-up date, so a NotReady reviewer without one (shouldn't happen, but
  // defensively) shows no banner rather than a blank one.
  const showNotReadyBanner = statusValue === ReviewerLifecycleStatus.NotReady && !!reviewer.followupDate;
  const formattedFollowUpDate = formatShortDateOnly(reviewer.followupDate);
  const canContinueOnboarding = isReviewerActionAvailable(ReviewerProfileActionKey.ContinueOnboarding, statusValue);
  const canEditFollowUpDate = isReviewerActionAvailable(ReviewerProfileActionKey.EditFollowUpDate, statusValue);
  const canMarkNotReady = isReviewerActionAvailable(ReviewerProfileActionKey.NotReady, statusValue);
  const canInvite = isReviewerActionAvailable(ReviewerProfileActionKey.Invite, statusValue);
  const canDiscard = isReviewerActionAvailable(ReviewerProfileActionKey.Discard, statusValue);
  const canArchive = isReviewerActionAvailable(ReviewerProfileActionKey.Archive, statusValue);
  const canReactivate = isReviewerActionAvailable(ReviewerProfileActionKey.Reactivate, statusValue);
  const canHold = isReviewerActionAvailable(ReviewerProfileActionKey.Hold, statusValue) && !isOnHold;
  const canRemoveHold = isReviewerActionAvailable(ReviewerProfileActionKey.RemoveHold, statusValue) && isOnHold;
  const canMarkProfileCompleted =
    isReviewerActionAvailable(ReviewerProfileActionKey.MarkProfileCompleted, statusValue) && !isProfileComplete;
  const missingRequiredSections = getReviewerProfileMissingSections(
    contactData,
    licenseData,
    ratesData,
    uploadedDocumentTypeNames
  );
  const isRequiredFieldsComplete = missingRequiredSections.length === 0;
  // Invite has no client-side gate (see handleInviteClick) — the banner only ever reflects
  // what the backend actually rejected, never a speculative client-side guess.
  const sectionsToDisplay = backendMissingSections ?? [];

  // Contact/License/Rates saves and document uploads all flow back into these same props (see
  // ReviewerProfile.tsx), so this fires after any of them succeed. It only prunes entries that are
  // now resolved — it can never introduce a section that wasn't already being displayed.
  useEffect(() => {
    setBackendMissingSections((prev) =>
      pruneResolvedMissingSections(
        prev,
        contactData,
        licenseData,
        ratesData,
        uploadedDocumentTypeNames,
        emailAtLastValidation
      )
    );
  }, [contactData, licenseData, ratesData, uploadedDocumentTypeNames, emailAtLastValidation]);

  const [postMarkProfileCompleted] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'markProfileCompleted'),
    undefined,
    true,
    '3.0'
  );

  const handleMarkProfileCompleted = async () => {
    setBackendMissingSections(null);
    const resp: any = await postMarkProfileCompleted({});
    if (resp?.status === 200) {
      toast.success('Reviewer marked as Completed');
      onReviewerRefetch?.();
      return;
    }

    const missingSections = getMissingSectionsFromErrorResponse(resp);
    if (missingSections) {
      toast.error('Please complete all required fields before marking this profile as completed.');
      setBackendMissingSections(missingSections);
      setEmailAtLastValidation(contactData?.email);
      setShowMissingFieldsAlert(true);
      return;
    }

    toast.error(resp?.response?.data?.message ?? 'Failed to mark profile as completed. Please try again.');
  };

  const [postContinueOnboarding] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'status'),
    undefined,
    true,
    '3.0'
  );

  // Instant action, no confirmation dialog — fires the transition immediately.
  const handleContinueOnboarding = async () => {
    setIsContinuingOnboarding(true);
    try {
      const resp: any = await postContinueOnboarding({ newStatus: { value: ReviewerLifecycleStatus.Onboarding } });
      if (resp?.status === 200) {
        toast.success('Reviewer status changed successfully');
        onReviewerRefetch?.();
      } else if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
      } else {
        toast.error(resp?.response?.data?.message ?? 'Failed to change reviewer status. Please try again.');
      }
    } finally {
      setIsContinuingOnboarding(false);
    }
  };

  const fullName = [reviewer.firstName, reviewer.lastName].filter(Boolean).join(' ') || '—';
  const statusLabel = reviewer.status?.displayName;

  const activeTab: ReviewerProfileTab =
    (tab as ReviewerProfileTab) ?? ReviewerProfileTab.Overview;

  const handleTabClick = (selected: ReviewerProfileTab) => {
    history.push(`${ROUTES.REVIEWER_PROFILE}/${id}/${selected}`);
  };

  const [postCheckProfileCompleteness] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'checkProfileCompleteness'),
    undefined,
    false,
    '3.0'
  );

  const handleInviteClick = async () => {
    // Runs the same completeness validation the dialog's own Invite button runs on submit, but
    // ahead of showing the dialog — mutates nothing, just probes. Reset any stale banner from a
    // previous attempt so it doesn't linger across a fresh check.
    setBackendMissingSections(null);
    setShowMissingFieldsAlert(false);
    setIsCheckingInviteCompleteness(true);
    try {
      const resp: any = await postCheckProfileCompleteness({});
      if (resp?.status === 200) {
        setIsInviteDialogOpen(true);
        return;
      }

      const missingSections = getMissingSectionsFromErrorResponse(resp);
      if (missingSections) {
        toast.error('Please complete all required fields before inviting this reviewer.');
        setBackendMissingSections(missingSections);
        setEmailAtLastValidation(contactData?.email);
        setShowMissingFieldsAlert(true);
        return;
      }

      if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
        return;
      }

      toast.error(resp?.response?.data?.message ?? 'Failed to invite reviewer. Please try again.');
    } finally {
      setIsCheckingInviteCompleteness(false);
    }
  };

  const handleInviteValidationError = (sections: MissingSectionError[]) => {
    setBackendMissingSections(sections);
    setEmailAtLastValidation(contactData?.email);
    setShowMissingFieldsAlert(true);
  };

  const handleMissingFieldSectionClick = (section: string) => {
    // Documents isn't a FormSection card on the Overview tab — it's a separate profile tab.
    if (section === REVIEWER_PROFILE_SECTION_DOCUMENTS) {
      if (activeTab !== ReviewerProfileTab.Documents) {
        history.push(`${ROUTES.REVIEWER_PROFILE}/${id}/${ReviewerProfileTab.Documents}`);
      }
      return;
    }

    const sectionId = section.replace(/\s+/g, '').toLowerCase();
    const scrollToSection = () => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    };

    if (activeTab !== ReviewerProfileTab.Overview) {
      history.push(`${ROUTES.REVIEWER_PROFILE}/${id}/${ReviewerProfileTab.Overview}`);
      setTimeout(scrollToSection, 100);
    } else {
      scrollToSection();
    }
  };

  const contactPref = contactData?.contactPreferenceDisplayName;
  const cellPhone = contactData?.cellPhone;
  const workPhone = contactData?.workPhone;
  const email = contactData?.email;
  const pocName = contactData?.additionalContactName;
  const pocPhone = contactData?.additionalContactPhone;
  const pocEmail = contactData?.additionalContactEmail;

  return (
    <div className="reviewer-profile-header">

      {/* Back button */}
      <button className="reviewer-profile-header__back" onClick={() => history.push(ROUTES.REVIEWER_PROFILE)}>
        <ArrowBackIcon />
        <span>Back</span>
      </button>

      {/* Main content */}
      <div className="reviewer-profile-header__content">

        {/* Profile row */}
        <div className="reviewer-profile-header__profile-row">

          {/* Avatar */}
          <div className="reviewer-profile-header__avatar">
            <span className="reviewer-profile-header__avatar-icon" aria-hidden />
          </div>

          {/* Info */}
          <div className="reviewer-profile-header__info">

            {/* Name + Status */}
            <div className="reviewer-profile-header__name-row">
              <h1 className="reviewer-profile-header__name">{fullName}</h1>
              {statusLabel && <StatusPill status={statusLabel} />}
              {canMarkProfileCompleted && <SecondaryStatusPill icon={<ErrorOutlineOutlinedIcon />} label="Incomplete" />}
              {isOnHold && <SecondaryStatusPill icon={<PauseCircleOutlinedIcon />} label="Hold" />}
            </div>

            {/* Contact details */}
            <div className="reviewer-profile-header__details">

              {/* Row 1: Contact Preference, C:, W:, Email */}
              <div className="reviewer-profile-header__detail-row">
                <div className="reviewer-profile-header__detail-field">
                  <CallIcon />
                  <span>Contact Preference: {contactPref}</span>
                </div>
                <div className="reviewer-profile-header__detail-value">
                  <span>C: {cellPhone}</span>
                </div>
                <div className="reviewer-profile-header__detail-value">
                  <span>W: {workPhone}</span>
                </div>
                {email && (
                  <div className="reviewer-profile-header__detail-value reviewer-profile-header__detail-value--wide">
                    <span>{email}</span>
                  </div>
                )}
              </div>

              {/* Row 2: Point of Contact + optional name, phone, email */}
              <div className="reviewer-profile-header__detail-row">
                <div className="reviewer-profile-header__detail-field">
                  <PersonIcon />
                  <span>Point of Contact:</span>
                </div>
                {pocName && (
                  <div className="reviewer-profile-header__detail-value">
                    <span>{pocName}</span>
                  </div>
                )}
                {pocPhone && (
                  <div className="reviewer-profile-header__detail-value">
                    <span>{pocPhone}</span>
                  </div>
                )}
                {pocEmail && (
                  <div className="reviewer-profile-header__detail-value reviewer-profile-header__detail-value--wide">
                    <span>{pocEmail}</span>
                  </div>
                )}
              </div>

            </div>

          </div>
        </div>

        {showMissingFieldsAlert && sectionsToDisplay.length > 0 && (
          <FormErrorSection
            sectionsWithErrors={sectionsToDisplay}
            onSectionClick={handleMissingFieldSectionClick}
          />
        )}

        {/* Tabs */}
        <div className="reviewer-profile-header__tabs">
          {TABS.map(({ tab, permissions }) => (
            <SecureComponentV3 key={tab} permissions={permissions} requireAll>
              <button
                className={`reviewer-profile-header__tab${activeTab === tab ? ' reviewer-profile-header__tab--selected' : ''}`}
                onClick={() => handleTabClick(tab)}
              >
                <span>{tab}</span>
              </button>
            </SecureComponentV3>
          ))}
        </div>

      </div>

      {/* Not Ready follow-up banner — overflows above the header's own top padding, pinned
          directly over the Profile Actions menu it relates to. */}
      {showNotReadyBanner && (
        <div className="reviewer-profile-header__not-ready-banner">
          <img src={CalendarClockIcon} alt="" aria-hidden />
          <span>Follow-up on {formattedFollowUpDate}</span>
        </div>
      )}

      {/* Profile Actions menu — rendered after __content to sit above it in stacking order */}
      <div className="reviewer-profile-header__actions">
        <ActionMenu
          label="Profile Actions"
          grantedPermissions={grantedPermissions}
          items={[
            ...(canInvite
              ? [{
                  label: 'Invite',
                  onClick: handleInviteClick,
                  permissions: [PermissionCodesV3.Reviewer.Status.Invite],
                  disabled: isCheckingInviteCompleteness,
                }]
              : []),
            ...(canContinueOnboarding
              ? [{
                  label: 'Continue Onboarding',
                  onClick: handleContinueOnboarding,
                  permissions: [PermissionCodesV3.Reviewer.Status.ContinueOnboarding],
                  disabled: isContinuingOnboarding,
                }]
              : []),
            ...(canMarkNotReady
              ? [{
                  label: 'Mark as Not Ready',
                  onClick: () => setIsNotReadyDialogOpen(true),
                  permissions: [PermissionCodesV3.Reviewer.Status.NotReady],
                }]
              : []),
            ...(canEditFollowUpDate
              ? [{
                  label: 'Edit Follow Up Date',
                  onClick: () => setIsEditFollowUpDrawerOpen(true),
                  permissions: [PermissionCodesV3.Reviewer.Status.EditFollowUpDate],
                }]
              : []),
            ...(canHold
              ? [{
                  label: 'Place on Hold',
                  onClick: () => setIsHoldDialogOpen(true),
                  permissions: [PermissionCodesV3.Reviewer.Status.Hold],
                }]
              : []),
            ...(canRemoveHold
              ? [{
                  label: 'Remove Hold',
                  onClick: () => setIsRemoveHoldDialogOpen(true),
                  permissions: [PermissionCodesV3.Reviewer.Status.RemoveHold],
                }]
              : []),
            ...(canArchive
              ? [{
                  label: 'Archive',
                  onClick: () => setIsArchiveDialogOpen(true),
                  permissions: [PermissionCodesV3.Reviewer.Status.Archive],
                }]
              : []),
            ...(canMarkProfileCompleted
              ? [{
                  label: 'Mark Profile Completed',
                  onClick: handleMarkProfileCompleted,
                  permissions: [PermissionCodesV3.Reviewer.Status.Complete],
                  disabled: !isRequiredFieldsComplete,
                }]
              : []),
            ...(canDiscard
              ? [{
                  label: 'Discard',
                  onClick: () => setIsDiscardDialogOpen(true),
                  permissions: [PermissionCodesV3.Reviewer.Status.Discard],
                }]
              : []),
            ...(canReactivate
              ? [{
                  label: 'Reactivate',
                  onClick: () => setIsReactivateDialogOpen(true),
                  permissions: [PermissionCodesV3.Reviewer.Status.Reactivate],
                }]
              : []),
          ]}
        />
      </div>

      <ReviewerProfileArchiveDialog
        reviewerId={id}
        archiveReasons={archiveReasons}
        dialogVisible={isArchiveDialogOpen}
        toggleDialog={() => setIsArchiveDialogOpen(false)}
        onSuccess={() => onReviewerRefetch?.()}
      />

      <ReviewerProfileReactivateDialog
        reviewerId={id}
        dialogVisible={isReactivateDialogOpen}
        toggleDialog={() => setIsReactivateDialogOpen(false)}
        onSuccess={() => onReviewerRefetch?.()}
      />

      <ReviewerProfilePlaceOnHoldDialog
        reviewerId={id}
        dialogVisible={isHoldDialogOpen}
        toggleDialog={() => setIsHoldDialogOpen(false)}
        onSuccess={() => onReviewerRefetch?.()}
      />

      <ReviewerProfileRemoveHoldDialog
        reviewerId={id}
        dialogVisible={isRemoveHoldDialogOpen}
        toggleDialog={() => setIsRemoveHoldDialogOpen(false)}
        onSuccess={() => onReviewerRefetch?.()}
      />

      <ReviewerProfileDiscardDialog
        reviewerId={id}
        dialogVisible={isDiscardDialogOpen}
        toggleDialog={() => setIsDiscardDialogOpen(false)}
        onSuccess={() => history.push(ROUTES.REVIEWER_PROFILE)}
      />

      <ReviewerProfileInviteDialog
        reviewerId={id}
        email={email}
        dialogVisible={isInviteDialogOpen}
        toggleDialog={() => setIsInviteDialogOpen(false)}
        onSuccess={() => onReviewerRefetch?.()}
        onValidationError={handleInviteValidationError}
      />

      <ReviewerProfileNotReadyDialog
        reviewerId={id}
        dialogVisible={isNotReadyDialogOpen}
        toggleDialog={() => setIsNotReadyDialogOpen(false)}
        onSuccess={() => onReviewerRefetch?.()}
      />

      <ReviewerProfileEditFollowUpDrawer
        reviewerId={id}
        followupDate={reviewer.followupDate}
        open={isEditFollowUpDrawerOpen}
        onClose={() => setIsEditFollowUpDrawerOpen(false)}
        onSuccess={() => onReviewerRefetch?.()}
      />

    </div>
  );
});

export default ReviewerProfileHeader;
